import {uuid} from 'uuidv4';
import {getMidiEffect} from "effects/midi/getMidiEffect.js";
import MidiTrigger from "effects/midi/MidiTrigger.js";
import ParameterContext from "effects/ParameterContext.js";
import {AbstractParameter} from "parameters/AbstractParameter.js";
import {
    SCRIPT,
    GATE_MASK,
    DURATION,
    DELAY,
    ORDER_OF_OPERATIONS,
    EVERY,
    REVERSE,
    TRANSPOSE,
    MARKOV,
    MARKOV_PREVIOUS,
    PROBABILITY} from './transformations.js';
import Tone from 'tone';
import {newStepGroup} from 'actions/index.js';

var counter=0;
let idToTransformation = {};

/**
 * Creates a group from the passed steps
 * @force: If force is true we skip the check of whether there is already a group
 */
export const groupSteps = (steps=[], track, force) => {
    if (force) {
        //  if its forced then we dont check for shit
    } else if (steps.every(x => x.group)) {
        // first check if these steps already contain a group
        // if so then we dont want to do anything
        return undefined;
    } else if (steps.some(x => x.group)) {
        // there are some groups but there also some non grouped ones
        // in this case we merge them into one of the groups
        let group = steps.find(x => x.group).group;
        // add all the steps to the group
        for (let step of steps) {
            if (step.group !== group) {
                group.steps.push(step);
                step.group = group;
            }

        }
        track.sequencerEngine.dispatch(
            newStepGroup(group));
        return group;
    }

    let group = newGroup(steps);
    steps.forEach(s => s.group = group);
    track.stepGroups.push(group);
    track.sequencerEngine.dispatch(
        newStepGroup(group));
    return group;
};

/**
 * creates a new empty group
 */
export const newGroup = (steps, track) => {
    let group = {
        id: uuid(),
        name: 'Group ' + (++counter),
        steps: steps,
        transformations: []
    };
    newTransformation(group, track);
    return group;
};

/**
 * Create a new empty tranformation
 */
export const newTransformation = (group, track=group.steps[0].track) => {
    let whenValue = newParam("Conditional ", track, 0, 100);
    let thenValue = newParam("Action Amount", track, -32, 32);
    let auxValue = newParam("Aux Value", track, 0, 100, 100);

    let transformation = {
        id: uuid(), // identifier for the transformation
        whenType: PROBABILITY, // What kind of division of time 
        whenValue, // WHEN a transformation should occur
        thenType: undefined, // What kinda of tranfsormation should occur
        thenValue, // how much of that transformation should occur
        probabilityBit: true, // a bit that keeps track of whether the transformation is ON/off
        loopCounter: -1, // a number that keeps track of how many loops have elapsed
        auxValue, // an auxiliary parameter (used in markov chains)
        auxProbabilityBit: true,
        midiEffect: undefined, // a midi effect that will be run 
    };
    idToTransformation[transformation.id] = transformation;
    group.transformations.push(transformation);
    return transformation;
};

export const ungroupSteps = (steps) => {
    steps.forEach(x => deleteStepFromGroup(x));
    if (steps[0] && steps[0].track) {
        steps[0].track.updateSteps();
    }
};

/**
 * executed at the beginning of a loop.
 * Set the loopCounter/probability bit to the value for that loop
 */
export const onNewLoop = (group) => {
    for (let transformation of group.transformations) {
        let {whenType, whenValue, auxValue} = transformation;
        transformation.loopCounter++;

        // keep track of previous bits
        transformation.previousProbabilityBit = transformation.probabilityBit;
        transformation.previousAuxBit = transformation.auxBit;

        if (whenType === PROBABILITY) {
            let rand = Math.random();
            let bit = rand  < (whenValue.get() / 100.0);
            transformation.probabilityBit = bit;
        } else if (whenType === MARKOV || whenType === MARKOV_PREVIOUS) {
            let auxBit = Math.random() < (Math.abs(auxValue.get()) / 100.0);
            transformation.auxProbabilityBit = auxBit;
        }
    }
};

/**
 * Returs whether we should run a transformation this loop cycle
 * Based on the probability bit and loopCounter (that was calculated in onNewLoop)
 */
export const shouldTransform = (transformation) => {
    if (!transformation) {
        return false;
    }

    let whenValue = transformation.whenValue.get();
    if (transformation.whenType === PROBABILITY) {
        // if probabilityBit is true then we want to mask
        return transformation.probabilityBit;
    } else if (transformation.whenType === EVERY) {
        return transformation.loopCounter % whenValue === whenValue - 1;
    } else if (transformation.whenType === MARKOV) {
        // MARKOV transformations are dependent on other
        // transformations to trigger. So we call shouldTransform
        // on the value
        // however, the value is gonna be an ID of the transform,
        // so we must first look up the transformation
        let dependency = idToTransformation[whenValue];
        let auxValue = transformation.auxValue.get();
        if (auxValue >= 0) {
            return transformation.auxProbabilityBit && shouldTransform(dependency);
        } else {
            // when aux value is negative, then we take the NOT of the dependent
            // transformation. i.e. when only run THIS transform when the
            // dependent transform DOES NOT occur.
            return transformation.auxProbabilityBit && !shouldTransform(dependency);
        }
    }
    return false;
};

/**
 * Transforms a step without mutating the original step data
 * Assumes that we should run the transform-- any checks
 * should be made before calling this
 */
export const transformStep = (step, group, time, stepNumber) => {
    if (group.transformations.every(t => !shouldTransform(t))) {
        return {step, time};
    }

    let transformations = [... group.transformations];
    transformations.sort(
        (a,b) =>
            ORDER_OF_OPERATIONS.indexOf(a.thenType) -
            ORDER_OF_OPERATIONS.indexOf(b.thenType));
    
    for (let t of transformations) {
        if (!shouldTransform(t)) {
            continue;
        }
        if (t.thenType === REVERSE) {
            step = reverse(step, group);
        } else if (t.thenType === TRANSPOSE) {
            step = transpose(step, t.thenValue.get());
        } else if (t.thenType === DURATION) {
            step = duration(step, t.thenValue.get());
        } else if (t.thenType === SCRIPT) {
            t.midiEffect.onNewStep();
            t.midiEffect.input(
                new MidiTrigger(step, time, stepNumber));
            let triggers = t.midiEffect.output(time, stepNumber, stepNumber);
            if (triggers.length === 0) {
                return {time, step};
            }
            return {time: triggers[0].time, step: triggers[0].stepData};
        }
    }

    return {time, step};
};

/**
 * Reverses the step data ordering in the group 
 */
export const reverse = (step, group) => {
    let index = group.steps.indexOf(step);
    let steps = [... group.steps];
    steps.reverse();
    return steps[index];
}

/**
 * Adds amt to the transpose of a step
 */
export const transpose = (step, amt) => {
    step = step.getCopy();
    step.transpose += amt;
    return step;
};

/**
 * Multiplies amt to the duration of a step
 */
export const duration = (step, amt) => {
    step = step.getCopy();
    step.duration *= amt;
    return step;
};

/**
 * Helper for creating a new parameter
 */
const newParam = (name, track, min, max, defaultValue=0) => {
    let param = new AbstractParameter(name, track, () => 0, {
        defaultValue,
        min,
        max,
        doNotDispose: true
    });
    param.setContext(
        new ParameterContext()
            .forTrack(track));
    return param;
};

export const getStepGroupJson = (group) => {
    let _group = {... group, transformations: [], steps: undefined};

    for (let transformation of group.transformations) {
        let _t = {...transformation, copiedFrom: undefined, copiedTo: undefined};
        _t.whenValue = transformation.whenValue.getJson();
        _t.thenValue = transformation.thenValue.getJson();
        _t.auxValue = transformation.auxValue.getJson();
        if (transformation.midiEffect) {
            _t.midiEffect = transformation.midiEffect.getJson();
        }
        _group.transformations.push(_t);
    }

    return _group;
};

export const fromStepGroupJson = (json, track) => {
    let group = newGroup([], track);
    group.name = json.name;
    group.id = json.id;

    let i=0;
    for (let transformation of json.transformations) {
        group.transformations[i].id = transformation.id;
        group.transformations[i].whenType = transformation.whenType;
        group.transformations[i].thenType = transformation.thenType;
        group.transformations[i].whenValue.fromJson(transformation.whenValue);
        group.transformations[i].thenValue.fromJson(transformation.thenValue);
        if (transformation.auxValue) {
            group.transformations[i].auxValue.fromJson(transformation.auxValue);
        }

        if (transformation.midiEffect) {
            group.transformations[i].midiEffect = getMidiEffect(
                transformation.midiEffect, track);
            group.transformations[i].midiEffect.fromJson(
                transformation.midiEffect);
        }

        idToTransformation[transformation.id] = group.transformations[i];
        if (i < json.transformations.length - 1) {
            newTransformation(group, track);
        }
        i++;
    }

    return group;
};

export const copyFromGroup = (groupFrom, groupTo, track) => {
    for (let i=0; i < groupFrom.transformations.length; i++) {
        let trFrom = groupFrom.transformations[i];
        let trTo = groupTo.transformations[i];
        trTo.whenType = trFrom.whenType;
        trTo.thenType = trFrom.thenType;
        trTo.whenValue.copyFrom(trFrom.whenValue);
        trTo.thenValue.copyFrom(trFrom.thenValue);
        if (trFrom.midiEffect) {
            trTo.midiEffect = trFrom.midiEffect.getCopy(track, true);
        }
        trFrom.copiedTo = trTo;
        trTo.copiedFrom = trFrom;

        let whenValue = trFrom.whenValue.get();
        if (trFrom.whenType === MARKOV && whenValue) {
            let dependency = idToTransformation[whenValue];
            if (dependency && dependency.copiedTo) {
                trTo.whenValue.set(dependency.copiedTo.id);
            }
        }

        if (i < groupFrom.transformations.length - 1) {
            // how do we mantain the dependencies though here???
            newTransformation(groupTo, track);
        }
    }
};

export const deleteStepFromGroup = (stepData) => {
    if (stepData.group) {
        stepData.group.steps = stepData.group.steps.filter(
            s => s !== stepData);
        stepData.group = undefined;
    }
};

export const shouldMask = (step) => {
    if (step.group) {
        let {group} = step;
        let transformation = group.transformations.find(t => t.thenType === GATE_MASK);
        if (!transformation) {
            return false;
        }
        return shouldTransform(transformation);
    }
    return false;
};
